/**
 * noticePeriods.js — PURE notice-of-termination maths for Irish tenancies.
 *
 * ── Why this module exists ─────────────────────────────────────────────
 * The public lead-magnet page at /tools/notice-calculator renders for a
 * logged-out cold click, so everything it imports lands in the initial
 * chunk. Same contract as rentCap.js: ZERO imports — no React, no MUI, no
 * Firebase, not even rentCap.js. The handful of date helpers below are
 * deliberately duplicated from rentCap.js (same semantics: 'YYYY-MM-DD'
 * strings parsed as UTC midnight, so the March/October clock changes can
 * never shift a day count).
 *
 * The tenant notice table is ALSO duplicated here rather than imported
 * from ./rtbCompliance.js (TENANT_NOTICE_PERIODS_BY_DURATION, verified
 * 2026-09-23). That file is shared by the authed product and pulls
 * ./rentCap; keeping this module import-free is worth one duplicated
 * six-row table. If the statutory table ever changes, change BOTH.
 *
 * ── The law encoded here (all checked 2026-09-27) ──────────────────────
 * PRIMARY: Residential Tenancies Act 2004, Revised Act "updated to
 * 1 March 2026", Law Reform Commission:
 *   https://revisedacts.lawreform.ie/eli/2004/act/27/revised/en/html
 *   (irishstatutebook.ie carries only the as-enacted text; the Revised Act
 *   is the consolidated one. The LRC page notes amendments made after
 *   1 March 2026 are not yet consolidated — see RTB_COPY below.)
 * SECONDARY (fetched and read in full, both agree with the Act):
 *   https://www.citizensinformation.ie/en/housing/renting-a-home/tenants-rights-and-responsibilities/if-your-landlord-wants-you-to-leave/
 *   https://www.citizensinformation.ie/en/housing/renting-a-home/changes-to-the-rules-for-renting-from-march-2026/
 *   https://rtb.ie/renting/ending-a-tenancy/notice-of-termination-guides/
 *   https://rtb.ie/renting/ending-a-tenancy/how-a-tenant-can-end-a-tenancy/
 *   https://rtb.ie/renting/how-a-landlord-can-end-a-tenancy/
 *   https://rtb.ie/wp-content/uploads/2026/02/Ending-a-Tenancy-Landlord-Guide-1.pdf
 *   https://rtb.ie/about/news/important-changes-to-rental-law-from-14-september-2026/
 *
 * 1. LANDLORD PERIODS — s.66 Table 1, as substituted 6 July 2022 by the
 *    Regulation of Providers of Building Works and Miscellaneous
 *    Provisions Act 2022, s.96: <6 months 90 days; 6 months–<1 year 152;
 *    1–<7 years 180; 7–<8 years 196; 8+ years 224. The Residential
 *    Tenancies (Miscellaneous Provisions) Act 2026 (in force 1 March 2026)
 *    did NOT change these periods — the Revised Act to 1 March 2026 still
 *    shows the 2022 table, and the RTB's Feb 2026 landlord guide says
 *    periods "are usually between 90 and 224 days". What the 2026 Act
 *    changed is the GROUNDS (point 5).
 *    NOTE: rtbCompliance.js's LANDLORD_NOTICE_PERIODS_2026 (flat 90 days
 *    for post-March-2026 tenancies) and TENANT_NOTICE_DAYS_2026 (flat 90)
 *    do not match any source found — flagged, not fixed here.
 *
 * 2. UNDER-6-MONTH MAXIMUM — a landlord's notice on a tenancy of less
 *    than 6 months is at least 90 and at most 120 days (Citizens
 *    Information "What are the notice periods?"; RTB NoT guide: "The
 *    maximum notice period to end a tenancy of less than six months is
 *    120 days"). Not yet visible in the LRC consolidation (post-1-March
 *    amendment); two independent secondary sources agree.
 *
 * 3. TENANT PERIODS — s.66 Table 2 (substituted 4 Dec 2015, unchanged
 *    since): <6 months 28; 6 months–<1 year 35; 1–<2 years 42; 2–<4 years
 *    56; 4–<8 years 84; 8+ years 112. The 2026 Act did not change them
 *    (CI: tenants "can end their tenancy at any time by giving ... the
 *    right amount of notice").
 *
 * 4. SHORT PERIODS
 *    Landlord, s.67(2): 7 days for serious anti-social behaviour (s.17(1)
 *    paras (a)/(b)) or behaviour threatening the fabric of the dwelling —
 *    no warning needed. 28 days, regardless of duration, for any other
 *    tenant breach, AFTER a written warning giving a reasonable time to
 *    remedy (s.34 Table para 1). 28 days for rent arrears ONLY if the
 *    s.67(3) condition is met: a written arrears notification to the
 *    tenant AND the RTB, and the arrears not paid within 28 days following
 *    receipt by the tenant or the RTB, whichever is later.
 *    Tenant, s.68(2): 7 days where the landlord's behaviour poses imminent
 *    danger of death/serious injury or to the fabric of the dwelling; 28
 *    days for any other landlord breach, after written notice to the
 *    landlord and a failure to remedy within a reasonable time (s.68(3)).
 *
 * 5. GROUNDS after 6 months (the Part 4 threshold):
 *    - Tenancy created BEFORE 1 March 2026 (s.34 Table): breach, arrears,
 *      no longer suitable, sale within 9 months, own/family use,
 *      substantial refurbishment, change of use. Same for every landlord.
 *    - Tenancy created ON/AFTER 1 March 2026 ("relevant Part 4 tenancy",
 *      s.35B inserted by the 2026 Act): becomes a Tenancy of Minimum
 *      Duration (6-year rolling cycles) after 6 months without a valid NoT.
 *        · Company, or landlord of more than 3 tenancies (s.35B(2)): may
 *          NOT use sale, own/family use, refurbishment or change of use at
 *          all. Only breach/arrears/anti-social and "no longer suitable".
 *        · Landlord of 1–3 tenancies, not a company (s.35B(3)–(5)):
 *          own/family use allowed during the cycle (immediate family only;
 *          wider family at the end of a cycle); sale DURING a cycle only on
 *          the hardship grounds in s.35B(4); sale, refurbishment and change
 *          of use otherwise only by a notice served before the cycle ends
 *          whose notice period expires on or after the cycle's end.
 *
 * 6. COUNTING — Day 1 of the notice period is the day AFTER the date of
 *    service (RTB sample Notice of Termination explanatory note: "Day 1 of
 *    the notice period begins on the day immediately following the date
 *    of service"; Interpretation Act 2005 s.18(h)). So N days' notice
 *    served on day S runs S+1 … S+N and the EARLIEST valid termination
 *    date is S + N calendar days. The tenant then has the whole 24 hours
 *    of that date to vacate (s.62(1)(f)).
 *
 * 7. DURATION — measured at the date of service, in completed calendar
 *    months from the tenancy start (month-ends clamped: 31 Aug + 6 months
 *    = 28/29 Feb). "Not less than 6 months" therefore starts ON the
 *    6-month anniversary.
 *
 * 8. RTB COPY — s.62(1A), inserted 28 Feb 2026 by the 2026 Act: landlord
 *    must serve a copy on the RTB the SAME DAY as the tenant, or the
 *    notice is invalid. From 14 September 2026 (Housing and Residential
 *    Tenancies (Miscellaneous Provisions) Act 2026) the copy must reach
 *    the RTB WITHIN 7 DAYS of serving the tenant (RTB news 31 Aug 2026;
 *    CI "Since 14 September 2026 ..."). The rule is picked by SERVICE
 *    date. Tenants do not have to copy the RTB.
 *    Service: from 14 Sept 2026 the RTB and CI advise email, hand delivery
 *    or leaving it at the address — NOT post.
 *
 * 9. VALIDITY — s.62(1): in writing; signed; date of service; prescribed
 *    form if any; reason (tenancies > 6 months, or the reason category for
 *    < 6 months, s.62(1)(da)); termination date + the whole-24-hours
 *    statement; statement that validity disputes go to the RTB within the
 *    s.80 period — 90 days from receipt, or 28 days where s.67/68 applies.
 *    Statutory declaration for sale / own-family use (s.34 paras 3, 4);
 *    statutory declaration + statement for refurbishment / change of use
 *    (paras 5, 6); statement for "no longer suitable" (para 2).
 *
 * 10. LICENCES — a room in the landlord's own home is outside the Act
 *     (s.3(2)(g): "a dwelling within which the landlord also resides").
 *     No statutory period: "reasonable notice", commonly 28 days (CI; the
 *     RTB threshold/registration guidance). No RTB copy, no RTB disputes.
 *
 * UNCERTAIN / NOT GUESSED: (a) whether "expiry" of a Tenancy of Minimum
 * Duration is the day before the 6th anniversary or the anniversary
 * itself — we use the anniversary, one day later, the safe side; (b) the
 * exact 120-day cap section number (post-consolidation). Every consumer
 * must say "guidance, not legal advice — check the RTB".
 */

// ── Constants ─────────────────────────────────────────────────────────

/** Date the 2026 Act's Tenancy-of-Minimum-Duration regime applies from. */
export const REFORM_DATE = '2026-03-01';

/** s.62(1A) same-day RTB copy — inserted 28 Feb 2026. */
export const RTB_COPY_SAME_DAY_FROM = '2026-02-28';

/** From this SERVICE date the RTB copy may follow within 7 days. */
export const RTB_COPY_7_DAYS_FROM = '2026-09-14';
export const RTB_COPY_DAYS = 7;

/** Length of a Tenancy of Minimum Duration cycle. */
export const TMD_YEARS = 6;

/** Part 4 threshold. */
export const PART4_MONTHS = 6;

/** s.66 Table 1 — landlord. minMonths = completed months at service. */
export const LANDLORD_NOTICE_TABLE = [
  { minMonths: 0, days: 90, label: 'Less than 6 months' },
  { minMonths: 6, days: 152, label: '6 months or more but less than 1 year' },
  { minMonths: 12, days: 180, label: '1 year or more but less than 7 years' },
  { minMonths: 84, days: 196, label: '7 years or more but less than 8 years' },
  { minMonths: 96, days: 224, label: '8 years or more' },
];

/** Landlord maximum for a tenancy of less than 6 months. */
export const UNDER_SIX_MONTHS_MAX_DAYS = 120;

/**
 * s.66 Table 2 — tenant. Duplicate of rtbCompliance.js
 * TENANT_NOTICE_PERIODS_BY_DURATION (verified there 2026-09-23); see the
 * module header for why it is not imported.
 */
export const TENANT_NOTICE_TABLE = [
  { minMonths: 0, days: 28, label: 'Less than 6 months' },
  { minMonths: 6, days: 35, label: '6 months or more but less than 1 year' },
  { minMonths: 12, days: 42, label: '1 year or more but less than 2 years' },
  { minMonths: 24, days: 56, label: '2 years or more but less than 4 years' },
  { minMonths: 48, days: 84, label: '4 years or more but less than 8 years' },
  { minMonths: 96, days: 112, label: '8 years or more' },
];

export const ANTISOCIAL_DAYS = 7;        // s.67(2)(a)
export const TENANT_BREACH_DAYS = 28;    // s.67(2)(aa)/(b) — tenant in breach
export const ARREARS_DAYS = 28;          // s.67(2)(aa) + s.67(3)
export const ARREARS_WARNING_DAYS = 28;  // s.67(3)(b)
export const LANDLORD_BREACH_DAYS = 28;  // s.68(2)(b) — tenant ends it
export const IMMINENT_DANGER_DAYS = 7;   // s.68(2)(a)
export const LICENCE_DAYS = 28;          // convention, not statute

export const DISPUTE_DAYS_STANDARD = 90; // s.80(b)
export const DISPUTE_DAYS_SHORT = 28;    // s.80(a)

export const LANDLORD_REASONS = [
  { value: 'sale', label: 'Selling the property', ground: 3 },
  { value: 'own_use', label: 'I or a family member will live there', ground: 4 },
  { value: 'refurbishment', label: 'Substantial refurbishment', ground: 5 },
  { value: 'change_of_use', label: 'Change of use', ground: 6 },
  { value: 'not_suitable', label: 'Property no longer suits the household’s size', ground: 2 },
  { value: 'breach', label: 'Tenant breach of obligations (after a warning)', ground: 1 },
  { value: 'arrears', label: 'Rent arrears (after the arrears warning)', ground: '1A' },
  { value: 'antisocial', label: 'Serious anti-social behaviour / threat to the property', ground: 1 },
];

export const TENANT_REASONS = [
  { value: 'standard', label: 'Standard — no reason needed' },
  { value: 'landlord_breach', label: 'Landlord in breach (after written notice)' },
  { value: 'imminent_danger', label: 'Imminent danger to life or the property' },
];

const NO_FAULT_GROUNDS = ['sale', 'own_use', 'refurbishment', 'change_of_use'];
const STAT_DEC_GROUNDS = ['sale', 'own_use', 'refurbishment', 'change_of_use'];

// ── Date helpers (duplicated from rentCap.js; UTC-midnight ISO strings) ──

/** Local (not UTC) calendar date as 'YYYY-MM-DD'. */
export function todayISO(now = new Date()) {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Parse 'YYYY-MM-DD' to a UTC-midnight Date; null if impossible. */
export function parseISO(value) {
  if (typeof value !== 'string') return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const y = Number(m[1]); const mo = Number(m[2]); const d = Number(m[3]);
  const date = new Date(Date.UTC(y, mo - 1, d));
  if (date.getUTCFullYear() !== y || date.getUTCMonth() !== mo - 1 || date.getUTCDate() !== d) {
    return null;
  }
  return date;
}

function iso(date) {
  return date ? date.toISOString().slice(0, 10) : null;
}

/** ISO date + n calendar days, as ISO. */
export function addDaysISO(value, days) {
  const d = parseISO(value);
  if (!d || !Number.isFinite(days)) return null;
  return iso(new Date(d.getTime() + days * 86400000));
}

/** Whole calendar days from a to b. */
export function daysBetweenISO(a, b) {
  const da = parseISO(a);
  const db = parseISO(b);
  if (!da || !db) return null;
  return Math.round((db - da) / 86400000);
}

/** ISO date + n months, clamped to month end (31 Aug + 6 = 28/29 Feb). */
export function addMonthsISO(value, months) {
  const d = parseISO(value);
  if (!d) return null;
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth();
  const lastDay = new Date(Date.UTC(y, m + months + 1, 0)).getUTCDate();
  return iso(new Date(Date.UTC(y, m + months, Math.min(d.getUTCDate(), lastDay))));
}

/** "16 September 2026". */
export function formatDateIE(value) {
  const d = parseISO(value);
  if (!d) return '';
  return d.toLocaleDateString('en-IE', {
    day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
  });
}

function later(a, b) {
  return parseISO(a) >= parseISO(b) ? a : b;
}

// ── Duration ──────────────────────────────────────────────────────────

/**
 * Completed calendar months from start to asAt (the largest m with
 * start + m months <= asAt), plus the leftover days. Null if asAt < start.
 */
export function tenancyDuration(startISO, asAtISO) {
  const s = parseISO(startISO);
  const a = parseISO(asAtISO);
  if (!s || !a || a < s) return null;
  let months = (a.getUTCFullYear() - s.getUTCFullYear()) * 12 + (a.getUTCMonth() - s.getUTCMonth());
  while (months > 0 && parseISO(addMonthsISO(startISO, months)) > a) months -= 1;
  const anchor = addMonthsISO(startISO, months);
  const days = daysBetweenISO(anchor, asAtISO);
  return { months, years: Math.floor(months / 12), remMonths: months % 12, days };
}

function plural(n, word) {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

/** "2 years, 3 months and 5 days" / "less than a day". */
export function describeDuration(dur) {
  if (!dur) return '';
  const parts = [];
  if (dur.years) parts.push(plural(dur.years, 'year'));
  if (dur.remMonths) parts.push(plural(dur.remMonths, 'month'));
  if (dur.days) parts.push(plural(dur.days, 'day'));
  if (!parts.length) return 'less than a day';
  if (parts.length === 1) return parts[0];
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

function lookup(table, months) {
  let row = table[0];
  for (const r of table) if (months >= r.minMonths) row = r;
  return row;
}

/** s.66 Table 1 row for a duration in completed months. */
export function landlordNoticeFor(months) {
  return lookup(LANDLORD_NOTICE_TABLE, months);
}

/** s.66 Table 2 row for a duration in completed months. */
export function tenantNoticeFor(months) {
  return lookup(TENANT_NOTICE_TABLE, months);
}

/**
 * End of the Tenancy-of-Minimum-Duration cycle running at `asAt`: the
 * first 6-year anniversary of the start strictly after `asAt`. Uses the
 * anniversary itself (the safe side of "expiry" — see module header).
 */
export function currentCycleEnd(startISO, asAtISO) {
  const a = parseISO(asAtISO);
  if (!parseISO(startISO) || !a) return null;
  for (let k = 1; k < 30; k += 1) {
    const end = addMonthsISO(startISO, TMD_YEARS * 12 * k);
    if (parseISO(end) > a) return end;
  }
  return null;
}

/** Which RTB-copy rule applies to a notice served on `servedISO`. */
export function rtbCopyRule(servedISO) {
  const s = parseISO(servedISO);
  if (!s) return null;
  if (s >= parseISO(RTB_COPY_7_DAYS_FROM)) {
    return {
      rule: 'within_7_days',
      deadline: addDaysISO(servedISO, RTB_COPY_DAYS),
      text: `Send a copy to the RTB within ${RTB_COPY_DAYS} days of serving the tenant — by `
        + `${formatDateIE(addDaysISO(servedISO, RTB_COPY_DAYS))} at the latest. If the RTB copy is `
        + 'late, the notice is invalid.',
    };
  }
  if (s >= parseISO(RTB_COPY_SAME_DAY_FROM)) {
    return {
      rule: 'same_day',
      deadline: servedISO,
      text: 'For a notice served before 14 September 2026 the copy had to reach the RTB on the '
        + `same day as the tenant (${formatDateIE(servedISO)}), or the notice was invalid.`,
    };
  }
  return {
    rule: 'check',
    deadline: null,
    text: 'The RTB-copy rules for a notice served before 28 February 2026 differ — check with the RTB.',
  };
}

// ── Checklists ────────────────────────────────────────────────────────

function landlordChecklist({ reason, underSix, disputeDays, rtb }) {
  const list = [
    'In writing (a letter or an email) and signed by you or your authorised agent',
    'The date of service — the day it is emailed, handed over or left at the tenant’s address',
    'The termination date (day, month and year), on or before which the tenant must leave',
    'A statement that the tenant has the whole 24 hours of the termination date to vacate',
    underSix
      ? 'Which category of reason applies (tenancies under 6 months need no specific ground, but the notice must say which category it falls in)'
      : 'The reason for ending the tenancy, citing the legal ground',
    `A statement that any dispute about the notice’s validity must be referred to the RTB within ${disputeDays} days of receiving it`,
  ];
  if (!underSix && STAT_DEC_GROUNDS.includes(reason)) {
    list.push('A statutory declaration, sworn before a solicitor, Commissioner for Oaths, notary or peace commissioner');
  }
  if (!underSix && (reason === 'refurbishment' || reason === 'change_of_use')) {
    list.push('A statement of the intended works or new use, with a copy of any planning permission');
  }
  if (!underSix && reason === 'refurbishment') {
    list.push('A certificate from a registered construction professional that the works require the dwelling to be vacated');
  }
  if (!underSix && reason === 'not_suitable') {
    list.push('A statement of why the property no longer suits the household (bed spaces, household size and make-up)');
  }
  if (reason === 'arrears') {
    list.push('Proof the rent-arrears warning went to both the tenant and the RTB, and that 28 days passed unpaid');
  }
  if (reason === 'breach') {
    list.push('A copy of the earlier written warning that gave the tenant a reasonable time to fix the breach');
  }
  list.push('Use the RTB’s template for your reason — email it, hand it over or leave it at the address (the RTB advises against post)');
  if (rtb) list.push(rtb.rule === 'within_7_days'
    ? 'A copy to the RTB within 7 days of serving the tenant (RTB Service Centre, NoticeofTermination@rtb.ie or post)'
    : rtb.text);
  return list;
}

function tenantChecklist({ reason, disputeDays }) {
  const list = [
    'In writing (a letter or an email) and signed by you (every tenant named on the tenancy, if joint)',
    'The date of service — the day you email it, hand it over or leave it at the landlord’s address',
    'The termination date (day, month and year) and that you have the whole 24 hours of it to leave',
    `A statement that any dispute about the notice’s validity must be referred to the RTB within ${disputeDays} days of receiving it`,
  ];
  if (reason !== 'standard') {
    list.push('The reason — the landlord’s breach, and a copy of your earlier written notice of it');
  } else {
    list.push('No reason is needed for a standard notice');
  }
  list.push('Keep proof of delivery. Tenants do not have to send a copy to the RTB.');
  return list;
}

// ── Main ──────────────────────────────────────────────────────────────

/**
 * @param {Object} input
 * @param {'landlord'|'tenant'} input.giver
 * @param {string} input.tenancyStartDate          'YYYY-MM-DD'
 * @param {string} input.servedDate                'YYYY-MM-DD'
 * @param {'tenancy'|'licence'} [input.agreementType]
 * @param {string} [input.reason]                  LANDLORD_REASONS / TENANT_REASONS value
 * @param {'small'|'large'|''} [input.landlordSize] 1–3 tenancies & not a company / company or 4+
 * @param {string} [input.arrearsWarningReceived]  Later of tenant/RTB receipt of the arrears warning
 * @param {string} [input.today]                   Override for tests
 */
export function calculateNotice(input = {}) {
  const {
    giver = 'landlord',
    tenancyStartDate,
    servedDate,
    agreementType = 'tenancy',
    landlordSize = '',
    arrearsWarningReceived = '',
    today = todayISO(),
  } = input;
  let { reason } = input;
  const errors = [];
  const warnings = []; // { level: 'error'|'warning'|'info', text }

  if (!parseISO(tenancyStartDate)) errors.push('Enter the date the tenancy (or licence) started.');
  if (!parseISO(servedDate)) errors.push('Enter the date the notice will be served.');
  if (!errors.length && parseISO(servedDate) < parseISO(tenancyStartDate)) {
    errors.push('The notice cannot be served before the tenancy started.');
  }
  if (errors.length) return { ok: false, errors, warnings };

  const duration = tenancyDuration(tenancyStartDate, servedDate);
  const base = {
    ok: true,
    errors,
    warnings,
    giver,
    agreementType,
    duration,
    durationText: describeDuration(duration),
    noticeStartDate: addDaysISO(servedDate, 1),
  };

  if (parseISO(servedDate) < parseISO(today)) {
    warnings.push({ level: 'info', text: `The service date is in the past — these are the rules that applied to a notice served on ${formatDateIE(servedDate)}.` });
  }

  // ── Licence: outside the Act ────────────────────────────────────────
  if (agreementType === 'licence') {
    warnings.push({
      level: 'warning',
      text: 'A licence (for example a room in the owner’s own home) is outside the Residential Tenancies Act, '
        + 'so there is no statutory notice period and no RTB process. The law requires “reasonable notice” — '
        + `${LICENCE_DAYS} days is the common convention — or whatever your agreement says, if longer. `
        + 'If the occupier actually has exclusive possession of a self-contained unit, it may be a tenancy '
        + 'after all, and the full rules apply.',
    });
    return {
      ...base,
      reason: null,
      noticeDays: LICENCE_DAYS,
      noticeBasis: 'Reasonable notice for a licence — 28 days is the usual convention, not a statutory figure.',
      bandLabel: null,
      isStatutory: false,
      earliestTerminationDate: addDaysISO(servedDate, LICENCE_DAYS),
      latestTerminationDate: null,
      rtbCopy: { required: false, rule: null, deadline: null, text: 'Not required — the RTB has no role in licences.' },
      disputeDays: null,
      disputeDeadline: null,
      isPostReform: null,
      cycleEnd: null,
      checklist: [
        'Give notice in writing and keep a copy, with the date you handed it over',
        'Say the date the licence ends and when the room must be vacated',
        'Follow any notice period in your licence agreement if it is longer than 28 days',
        'Return any deposit promptly, less only properly documented deductions',
      ],
    };
  }

  const underSix = duration.months < PART4_MONTHS;
  const isPostReform = parseISO(tenancyStartDate) >= parseISO(REFORM_DATE);

  // ── Tenant gives notice ─────────────────────────────────────────────
  if (giver === 'tenant') {
    if (!TENANT_REASONS.some((r) => r.value === reason)) reason = 'standard';
    const row = tenantNoticeFor(duration.months);
    let noticeDays = row.days;
    let noticeBasis = `Tenancy of ${describeDuration(duration)} at the date of service — `
      + `“${row.label}” in the statutory table (s.66, Table 2): ${row.days} days.`;
    let disputeDays = DISPUTE_DAYS_STANDARD;
    if (reason === 'landlord_breach') {
      noticeDays = LANDLORD_BREACH_DAYS;
      disputeDays = DISPUTE_DAYS_SHORT;
      noticeBasis = `Landlord in breach: ${LANDLORD_BREACH_DAYS} days, whatever the length of the tenancy (s.68).`;
      warnings.push({
        level: 'warning',
        text: 'The 28-day period only applies if you first told the landlord about the breach IN WRITING and '
          + 'they did not fix it within a reasonable time. Without that, the standard '
          + `${row.days}-day period applies.`,
      });
    } else if (reason === 'imminent_danger') {
      noticeDays = IMMINENT_DANGER_DAYS;
      disputeDays = DISPUTE_DAYS_SHORT;
      noticeBasis = `Imminent danger: ${IMMINENT_DANGER_DAYS} days, where the landlord’s behaviour poses an imminent `
        + 'danger of death or serious injury, or to the fabric of the dwelling (s.68).';
      warnings.push({
        level: 'warning',
        text: 'Seven days is for genuine emergencies only. If the landlord disputes it and the RTB disagrees, '
          + `you could be liable for the ${row.days} days’ notice you should have given.`,
      });
    }
    if (isPostReform && !underSix) {
      warnings.push({
        level: 'info',
        text: 'Your tenancy began on or after 1 March 2026, so it is a 6-year Tenancy of Minimum Duration. '
          + 'That ties the landlord, not you: you can leave at any time with the notice shown.',
      });
    }
    return {
      ...base,
      reason,
      noticeDays,
      noticeBasis,
      bandLabel: row.label,
      isStatutory: true,
      earliestTerminationDate: addDaysISO(servedDate, noticeDays),
      latestTerminationDate: null,
      rtbCopy: { required: false, rule: null, deadline: null, text: 'Not required — only a landlord’s notice has to be copied to the RTB.' },
      disputeDays,
      disputeDeadline: addDaysISO(servedDate, disputeDays),
      isPostReform,
      cycleEnd: null,
      checklist: tenantChecklist({ reason, disputeDays }),
    };
  }

  // ── Landlord gives notice ───────────────────────────────────────────
  if (!LANDLORD_REASONS.some((r) => r.value === reason)) reason = 'sale';
  const row = landlordNoticeFor(duration.months);
  let noticeDays = row.days;
  let noticeBasis = `Tenancy of ${describeDuration(duration)} at the date of service — `
    + `“${row.label}” in the statutory table (s.66, Table 1): ${row.days} days.`;
  let disputeDays = DISPUTE_DAYS_STANDARD;
  let latestTerminationDate = null;
  let earliest;
  let cycleEnd = null;

  if (reason === 'antisocial') {
    noticeDays = ANTISOCIAL_DAYS;
    disputeDays = DISPUTE_DAYS_SHORT;
    noticeBasis = `Serious anti-social behaviour or behaviour threatening the property: ${ANTISOCIAL_DAYS} days, `
      + 'whatever the length of the tenancy (s.67). No warning notice is needed.';
    warnings.push({
      level: 'warning',
      text: 'Seven days is only for SERIOUS anti-social behaviour — violence or threats of it, serious '
        + 'intimidation, drug dealing — or behaviour threatening the fabric of the property. For lesser '
        + 'nuisance you must follow the breach route: a written warning first, then 28 days.',
    });
  } else if (reason === 'breach') {
    noticeDays = TENANT_BREACH_DAYS;
    disputeDays = DISPUTE_DAYS_SHORT;
    noticeBasis = `Breach of tenant obligations: ${TENANT_BREACH_DAYS} days, whatever the length of the tenancy (s.67).`;
    warnings.push(underSix
      ? {
        level: 'info',
        text: 'For a tenancy under 6 months the law does not require a prior warning for a breach, but giving '
          + 'one in writing first makes the notice much easier to defend at the RTB.',
      }
      : {
        level: 'warning',
        text: 'Before this notice you must have given the tenant a WRITTEN WARNING describing the breach, '
          + 'saying you may end the tenancy if it is not fixed, and allowing a reasonable time to fix it. '
          + 'The RTB’s landlord guide says this warning need not go to the RTB; Citizens Information says '
          + 'it should — sending the RTB a copy costs you nothing.',
      });
  } else if (reason === 'arrears') {
    noticeDays = ARREARS_DAYS;
    disputeDays = DISPUTE_DAYS_SHORT;
    noticeBasis = `Rent arrears: ${ARREARS_DAYS} days, whatever the length of the tenancy (s.67) — but only after the statutory warning process.`;
    const warn = parseISO(arrearsWarningReceived) ? arrearsWarningReceived : null;
    if (warn) {
      // s.67(3)(b): unpaid "within the period of 28 days following" the later
      // receipt. Day 1 is the day after receipt, so the window closes at the
      // end of receipt + 28; the notice can be served from receipt + 29.
      const earliestServe = addDaysISO(warn, ARREARS_WARNING_DAYS + 1);
      if (parseISO(servedDate) < parseISO(earliestServe)) {
        warnings.push({
          level: 'error',
          text: `Too early. The tenant has 28 days after receiving the arrears warning (${formatDateIE(warn)}) to pay. `
            + `The earliest you can serve this notice is ${formatDateIE(earliestServe)} — a notice served sooner is invalid.`,
        });
      } else {
        warnings.push({
          level: 'info',
          text: `The 28-day payment window after the arrears warning closed on ${formatDateIE(addDaysISO(warn, ARREARS_WARNING_DAYS))}. `
            + 'If the arrears are still unpaid, you can serve this notice.',
        });
      }
    } else {
      warnings.push({
        level: 'warning',
        text: 'You cannot use the 28-day arrears notice until you have (1) sent a written rent-arrears warning '
          + 'stating the amount owed to BOTH the tenant and the RTB, and (2) waited 28 days from the later of '
          + 'the two receiving it. Enter the date the warning was received to check your timing.',
      });
    }
  } else if (underSix) {
    latestTerminationDate = addDaysISO(servedDate, UNDER_SIX_MONTHS_MAX_DAYS);
    noticeBasis += ` For a tenancy under 6 months the notice can be no longer than ${UNDER_SIX_MONTHS_MAX_DAYS} days.`;
  }
  earliest = addDaysISO(servedDate, noticeDays);

  // ── Grounds ─────────────────────────────────────────────────────────
  const noFault = NO_FAULT_GROUNDS.includes(reason);
  if (underSix && !['breach', 'arrears', 'antisocial'].includes(reason)) {
    warnings.push({
      level: 'info',
      text: 'In the first 6 months a landlord can end a tenancy for any reason, but the notice must still '
        + 'say which category the reason falls in. Serve it before the 6-month mark: once the tenancy '
        + `reaches 6 months (${formatDateIE(addMonthsISO(tenancyStartDate, PART4_MONTHS))}) the tenant gains security of tenure `
        + `${isPostReform ? '(a 6-year Tenancy of Minimum Duration)' : '(Part 4 rights)'} and the notice period jumps to 152 days.`,
    });
  } else if (!underSix && isPostReform) {
    cycleEnd = currentCycleEnd(tenancyStartDate, servedDate);
    if (noFault) {
      if (landlordSize === 'large') {
        warnings.push({
          level: 'error',
          text: 'Not a permitted ground. This tenancy began on or after 1 March 2026, and a company or a landlord '
            + 'with 4 or more tenancies cannot end a Tenancy of Minimum Duration to sell, move in, refurbish or '
            + 'change use — only for tenant breach, rent arrears, anti-social behaviour, or because the property '
            + 'no longer suits the household’s size.',
        });
      } else if (landlordSize === 'small') {
        if (reason === 'own_use') {
          warnings.push({
            level: 'info',
            text: 'As a landlord with 1–3 tenancies (not a company) you may end a Tenancy of Minimum Duration for '
              + 'your own use or an immediate family member’s (spouse, civil partner, child, parent). Wider family '
              + `(siblings, grandchildren, nephews, nieces) qualify only at the end of the 6-year cycle (${formatDateIE(cycleEnd)}). `
              + 'The statutory declaration must confirm you are not a company and have no more than 3 tenancies.',
          });
        } else {
          const beforeCycle = earliest;
          earliest = later(earliest, cycleEnd);
          const moved = earliest !== beforeCycle;
          warnings.push({
            level: 'warning',
            text: `During the 6-year cycle a landlord with 1–3 tenancies cannot end the tenancy to ${
              reason === 'sale' ? 'sell' : reason === 'refurbishment' ? 'refurbish' : 'change the use'
            }. The notice must be served before the cycle ends and its notice period must run to the end of the `
              + `cycle (${formatDateIE(cycleEnd)}) or later.${moved ? ' The earliest termination date below has been moved to the cycle end.' : ''}`
              + (reason === 'sale'
                ? ' Exception: you may sell during the cycle to avoid undue hardship — to fund your own home, to pay a '
                  + 'legally due debt of at least 15% of the sale price within 9 months, or in insolvency — backed by a '
                  + `statutory declaration. If that applies, the earliest date is ${formatDateIE(beforeCycle)}.`
                : ''),
          });
        }
      } else {
        warnings.push({
          level: 'warning',
          text: 'This tenancy began on or after 1 March 2026, so which reasons you may use depends on your size. '
            + 'A company or a landlord with 4+ tenancies cannot sell, move in, refurbish or change use at all; '
            + 'a landlord with 1–3 tenancies faces limits during each 6-year cycle. Choose your landlord type above.',
        });
      }
    }
  } else if (!underSix && !isPostReform && noFault) {
    warnings.push({
      level: 'info',
      text: 'This tenancy began before 1 March 2026, so the 2026 size-based limits do not apply. Sale (within '
        + '9 months), own or family use, substantial refurbishment and change of use are all permitted grounds '
        + 'for any landlord, with the statutory declaration. You must offer the tenancy back if the reason '
        + 'falls through (e.g. the property is re-let within 12 months).'
        + (reason === 'sale' ? ' Selling 10 or more units in one development within 6 months is restricted.' : ''),
    });
  }
  if (noFault || reason === 'not_suitable') {
    warnings.push({
      level: 'info',
      text: 'After a no-fault termination — including one in the first 6 months — you cannot reset the rent '
        + 'to market rate for the next tenancy. A fixed-term lease generally cannot be ended early by the '
        + 'landlord except for a tenant breach.',
    });
  }

  const rtb = rtbCopyRule(servedDate);
  return {
    ...base,
    reason,
    noticeDays,
    noticeBasis,
    bandLabel: row.label,
    isStatutory: true,
    earliestTerminationDate: earliest,
    latestTerminationDate,
    rtbCopy: { required: true, ...rtb },
    disputeDays,
    disputeDeadline: addDaysISO(servedDate, disputeDays),
    isPostReform,
    cycleEnd,
    checklist: landlordChecklist({ reason, underSix, disputeDays, rtb }),
  };
}
